
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { authRateLimiter, formatTimeUntilReset } from '@/utils/rateLimiter';
import { isValidEmail, validatePasswordStrength, sanitizeText } from '@/utils/validation';

export interface UsePasswordResetReturn {
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  validatePassword: (password: string) => { valid: boolean; errors: string[] };
  isLoading: boolean;
}

export const usePasswordReset = (): UsePasswordResetReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const validatePassword = (password: string) => {
    return validatePasswordStrength(password);
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      console.log("Sending password reset email to:", email);
      
      if (!email) {
        toast({
          title: 'Missing email',
          description: 'Please provide your email address',
          variant: 'destructive',
        });
        return { success: false, error: 'Email is required' };
      }

      if (!isValidEmail(email)) {
        toast({
          title: 'Invalid email',
          description: 'Please provide a valid email address',
          variant: 'destructive',
        });
        return { success: false, error: 'Invalid email format' };
      }

      const sanitizedEmail = sanitizeText(email);
      
      const rateLimitKey = `reset:${sanitizedEmail.toLowerCase()}`;
      const rateLimitResult = authRateLimiter.check(rateLimitKey);
      
      if (!rateLimitResult.allowed) {
        const timeUntilReset = formatTimeUntilReset(rateLimitResult.resetAt || 0);
        toast({
          title: 'Too many reset attempts',
          description: `Please try again after ${timeUntilReset}`,
          variant: 'destructive',
        });
        return { success: false, error: 'Rate limit exceeded' };
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error("Password reset error:", error);
        toast({
          title: 'Error sending reset link',
          description: error.message,
          variant: 'destructive',
        });
        return { success: false, error: error.message };
      }

      console.log("Password reset email sent successfully");
      toast({
        title: 'Reset link sent',
        description: 'Check your email for a link to reset your password',
      });
      return { success: true };
    } catch (error: any) {
      console.error('Unexpected error during password reset:', error);
      toast({
        title: 'Error sending reset link',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      return { success: false, error: error.message || 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  const confirmPasswordReset = async (token: string, newPassword: string) => {
    try {
      setIsLoading(true);
      console.log("Confirming password reset");

      if (!token || typeof token !== 'string') {
        toast({
          title: 'Invalid token',
          description: 'The password reset token is invalid or missing',
          variant: 'destructive',
        });
        return { success: false, error: 'Invalid reset token' };
      }

      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.valid) {
        toast({
          title: 'Password too weak',
          description: passwordValidation.errors.join('. '),
          variant: 'destructive',
        });
        return { success: false, error: 'Password does not meet the requirements' };
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error("Password update error:", error);
        toast({
          title: 'Error resetting password',
          description: error.message,
          variant: 'destructive',
        });
        return { success: false, error: error.message };
      }

      console.log("Password reset successful");
      toast({
        title: 'Password updated',
        description: 'Your password has been successfully reset',
      });
      
      return { success: true };
    } catch (error: any) {
      console.error('Unexpected error during password reset confirmation:', error);
      toast({
        title: 'Error resetting password',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
      return { success: false, error: error.message || 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    resetPassword,
    confirmPasswordReset,
    validatePassword,
    isLoading
  };
};
